// externals
import { useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

// libraries
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';

/**
 * Wraps an action creator so a field can show its own "recalculating" spinner, without adding any
 * per-field state to `usePaymentCall` itself - that hook is shared by every payment field in the app,
 * so this lives next to it instead of inside it. `isCalculatingPayment` is deal-wide, so wrapping the
 * action creator lets this field track only the calls its own change started.
 *
 * Claiming and releasing the spinner are both driven off `isCalculatingPayment` - no timer:
 *  - Releasing was already selector-driven: the effect below clears the claim the moment
 *    `isCalculatingPayment` transitions from true to false.
 *  - Claiming reads the same selector directly off the store in a microtask queued right after this
 *    wraps the dispatch. The middleware that starts a payment call flips `isCalculatingPayment` to
 *    true synchronously, before the call itself goes out, so by the time the microtask runs - strictly
 *    after the dispatch this wraps has finished - the store already reflects whether a call actually
 *    started. A dispatch that turns out to be a no-op (unchanged value, invalid deal, etc.) never flips
 *    it, so this never claims a call that doesn't exist and never needs to un-claim one later.
 */
export const useFieldUpdatingIndicator = <Args extends unknown[]>(actionCreator: (...args: Args) => unknown) => {
    const store = useStore();
    const isCalculatingPayment = useSelector(paymentSelectors.isCalculatingPayment);
    const wasCalculatingPayment = useRef(isCalculatingPayment);
    const [hasClaimedCall, setHasClaimedCall] = useState(false);

    const trackedActionCreator = (...args: Args) => {
        const action = actionCreator(...args);
        queueMicrotask(() => {
            if (paymentSelectors.isCalculatingPayment(store.getState() as StateTree)) {
                setHasClaimedCall(true);
            }
        });
        return action;
    };

    useEffect(() => {
        const wasCalculating = wasCalculatingPayment.current;
        wasCalculatingPayment.current = isCalculatingPayment;

        if (wasCalculating && !isCalculatingPayment) {
            setHasClaimedCall(false);
        }
    }, [isCalculatingPayment]);

    return {
        actionCreator: trackedActionCreator,
        isUpdating: hasClaimedCall && isCalculatingPayment
    };
};
